import { all, call, delay, put, select, take, takeLatest } from "redux-saga/effects";
import { generatePath } from "react-router-dom";
import { saveAs } from "file-saver";
import { Intent } from "@blueprintjs/core";
import BackendClient from "api/backend-client";
import { CommonApiMethods, PointScalingApiMethods } from "api/api-methods";
import { getCallbacks } from "api/ps-socket-callbacks";
import WSService from "api/web-socket.service";
import { psSocketUrl } from "api/url-builder";
import Toaster, { toastIcons } from "components/Page/components/Toaster";
import { preparePlotData as prepareLineGraphData } from "pages/PS-Workspace/components/ResultSetDetails/components/LineGraph/utils";
import { preparePlotData as prepareHeatMapData } from "pages/PS-Workspace/components/ResultSetDetails/components/HeatMap/utils";
import RoutePaths from "routes/route-paths";
import logger from "utils/logger";
import { getNewItemId } from "utils/ps-workspaceHelpers";
import { getCurrentDateTimeStamp } from "utils/dateTime";
import SocketChannel from "../socket-channel";
import { waitForLogin } from "../auth/auth.saga";
import { setDecisionTreeModalOpen } from "../global/global.actions";
import { toggleNoSeatsToastVisible, changeSeatToastVisibleStatus } from "../licensing/licensing.actions";
import { noSeatsAvailableSelector } from "../licensing/licensing.selectors";
import { loadLicensesListSaga } from "../licensing/licensing.saga";
import { SeatsToastVisibleStatusType } from "../licensing/licensing.types";
import { addSocketPendingOperation, createWorkspaceFailure, createWorkspaceSuccessful, exportResultsFinish, getMetricTemplateAnnotationsFailure, linkTagsFailure, linkTagsSuccessful, loadWorkspaceFailure, loadWorkspaceLayoutFailure, loadWorkspaceLayoutSuccessful, loadWorkspaceSuccessful, removePrintCanvasContent, removeResultElementsWithLongCalculation, removeSocketPendingOperation, resetSocketPendingOperations, scenarioExportFinish, setPrintCanvasContent, setResultElementWithLongCalculation, setResultSetElement, setResultSetId, setScenarioEntity, setScenarioId, setSocketError, setSocketStatus, setWorkspaceServerError, subscribeWorkspaceSuccessful, unlinkTagsFailure, unlinkTagsSuccessful, unsubscribeWorkspace, updateWorkspaceDescriptionFailure, updateWorkspaceDescriptionSuccessful, updateWorkspaceFailure, updateWorkspaceNameFailure, updateWorkspaceNameSuccessful, updateWorkspaceTagsSuccessful, WorkspaceActionType, } from "./ps-workspace.actions";
import { PsCalculationResultsDataItemState, EngineResultType, LONG_CALCULATION_DELAY, ScenarioEntityType, SocketStatus, } from "./ps-workspace.types";
import { calculationResultsHeatmapSelector, calculationResultsLineGraphSelector, calculationResultsMetadataSelector, calculationResultsTabularSelector, duplicatedResultSetIdSelector, getWorkspaceStateSelector, heatmapElementExportDataSelector, isSubscribedSelector, lineGraphElementExportDataSelector, metadataResultSetElementByIdSelector, pendingSocketOperationByRequestIdSelector, tabularViewLayoutSelector, workspaceIdSelector, workspaceLayoutSelector, } from "./ps-workspace.selectors";
import { RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS, SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS, UPDATE_WORKSPACE_SOCKET_METHODS, } from "./ps-workspace.update.types";
import { convertHeatmapResultsToCsv, convertLinegraphResultsToCsv, convertMetadataToCsv, convertTabularResultsToCsv, getGraphPrintContent, prepareWorkspaceLayout, } from "./ps-workspace.utils";
import { clearCreateWorkspaceForm, loadWorkspacesList } from "../ps-workspace-list/ps-workspace-list.actions";
import { loadWorkspacesSaga } from "../ps-workspace-list/ps-workspace-list.saga";
import { loadItemErrorHandler } from "../knowledge-base-item/knowledge-base-item.saga";
import { setSeats } from "store/licensing/licensing.actions";
import { apiErrorHandler } from "store/global/global.saga";
import { navigate } from "shared/navigation";
const SocketOperationNotificationMessages = {
    [SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateAnalysis]: "Scenario has successfully been duplicated",
    [RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateResultSet]: "Result Set has successfully been duplicated",
    [RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateResultSetElement]: "Result Element has successfully been duplicated",
    [SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS.ImportAnalysis]: "Scenario has successfully been imported",
};
function* subscribeWorkspaceSaga() {
    try {
        const workspace = yield select(getWorkspaceStateSelector);
        const isSubscribed = yield select(isSubscribedSelector);
        if (!isSubscribed) {
            const clientId = WSService.getInstance().getClientId();
            yield call([WSService.getInstance(), "send"], "Subscribe", { workspaceId: workspace.id, clientId });
        }
    }
    catch (error) {
        logger.error(error);
    }
}
function* unsubscribeWorkspaceSaga() {
    try {
        yield call([WSService.getInstance(), "stop"]);
        yield put(unsubscribeWorkspace());
        yield put(setSocketError(null));
        yield put(resetSocketPendingOperations());
    }
    catch (error) {
        logger.error(error);
    }
}
function* goBackHandleSaga() {
    try {
        yield call(unsubscribeWorkspaceSaga);
        yield put(loadWorkspacesList());
    }
    catch (error) {
        logger.error(error);
    }
}
function* connectChannelSaga() {
    try {
        yield call([WSService.getInstance(), "init"], psSocketUrl);
        yield call([SocketChannel.getInstance(), "createSocketChannel"], WSService.getInstance(), getCallbacks);
        yield call([WSService.getInstance(), "start"]);
        yield call(subscribeWorkspaceSaga);
        while (true) {
            const action = yield take(SocketChannel.getInstance().getChannel());
            yield put(action);
        }
    }
    catch (error) {
        logger.error(error);
    }
}
function* resubscribeWorkspaceSaga() {
    try {
        yield put(setSocketError(null));
        yield call(subscribeWorkspaceSaga);
    }
    catch (error) {
        logger.error(error);
    }
}
function* handleSubscribeMessageStatus({ payload }) {
    try {
        const requestId = yield call([WSService.getInstance(), "getLastRequestId"]);
        if (requestId === payload.requestId) {
            yield put(setSeats(payload.seats));
            const backendClient = BackendClient.getInstance();
            const workspace = yield select(getWorkspaceStateSelector);
            if (workspace.id !== null) {
                yield call([backendClient, CommonApiMethods.SetUserMetric], workspace.id);
            }
            yield call(loadWorkspacesSaga);
            yield put(changeSeatToastVisibleStatus(SeatsToastVisibleStatusType.OCCUPIED));
            yield put(changeSeatToastVisibleStatus(false));
            yield put(subscribeWorkspaceSuccessful());
        }
    }
    catch (error) {
        logger.error(error);
    }
}
function* setInitialAnalyseData(data) {
    const initialAnalyse = data.workspace?.analyses[0];
    const initialResultSet = data.workspace?.resultSets[0];
    yield put(setScenarioId(initialAnalyse ? { id: initialAnalyse.id } : null));
    yield put(setResultSetId(initialResultSet ? { id: initialResultSet.id } : null));
}
function* loadWorkspace({ payload }) {
    try {
        yield call(waitForLogin);
        yield call(unsubscribeWorkspaceSaga);
        const backendClient = BackendClient.getInstance();
        yield call(loadLicensesListSaga);
        yield call(loadWorkspacesSaga);
        const noSeatsAvailable = yield select(noSeatsAvailableSelector);
        if (noSeatsAvailable) {
            yield put(toggleNoSeatsToastVisible(true));
            return;
        }
        const data = yield call([backendClient, PointScalingApiMethods.GetPsWorkspace], payload) || [];
        const trimmedData = { ...data, workspace: null };
        yield put(loadWorkspaceSuccessful({ ...trimmedData, id: payload }));
        yield call(connectChannelSaga);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace list load failed");
        yield call(loadItemErrorHandler, e);
        yield put(loadWorkspaceFailure());
    }
}
function* loadWorkspaceLayout({ payload }) {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, PointScalingApiMethods.GetPsWorkspaceLayout], payload);
        if (data) {
            yield put(loadWorkspaceLayoutSuccessful(data));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace layout load failed");
        yield put(loadWorkspaceLayoutFailure(e));
    }
}
function* createWorkspace({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        const { workspaceId: id } = yield call([backendClient, PointScalingApiMethods.SavePsWorkspace], payload);
        yield put(clearCreateWorkspaceForm());
        yield put(setDecisionTreeModalOpen(false));
        yield put(createWorkspaceSuccessful());
        yield put(loadWorkspacesList());
        navigate(generatePath(RoutePaths.PointScalingWorkspace, { id }));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace creation is failed");
        yield put(createWorkspaceFailure());
        yield put(setWorkspaceServerError(e));
    }
}
function* updateWorkspace({ payload, }) {
    try {
        let commands;
        yield put(setSocketError(null));
        yield put(setSocketStatus(SocketStatus.UPDATING));
        const requestId = WSService.getInstance().getCurrentRequestId();
        if (Array.isArray(payload)) {
            commands = payload;
            yield all(commands.map((command) => put(addSocketPendingOperation({ ...command, requestId }))));
        }
        else {
            commands = [payload];
            yield put(addSocketPendingOperation({ ...payload, requestId }));
        }
        yield all(commands.map(({ method, data }) => call([WSService.getInstance(), "send"], method, data)));
    }
    catch (e) {
        yield put(updateWorkspaceFailure(e));
        logger.error("workspace update is failed");
    }
}
function* updateWorkspaceNameSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield call([backendClient, PointScalingApiMethods.UpdatePsWorkspaceName], payload);
        yield put(updateWorkspaceNameSuccessful(payload.name));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace name update is failed");
        yield put(updateWorkspaceNameFailure(e));
    }
}
function* updateWorkspaceDescriptionSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield call([backendClient, PointScalingApiMethods.UpdatePsWorkspaceDescription], payload);
        yield put(updateWorkspaceDescriptionSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "workspace description update is failed");
        yield put(updateWorkspaceDescriptionFailure(e));
    }
}
function* showSocketOperationNotificationsSaga(payload) {
    try {
        const incomingOperation = yield select(pendingSocketOperationByRequestIdSelector(payload.requestId));
        const methods = [
            SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateAnalysis,
            SCENARIO__UPDATE_WORKSPACE_SOCKET_METHODS.ImportAnalysis,
            RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateResultSet,
            RESULT_SET__UPDATE_WORKSPACE_SOCKET_METHODS.DuplicateResultSetElement,
        ];
        if (!incomingOperation || !methods.includes(incomingOperation.method)) {
            return;
        }
        const message = SocketOperationNotificationMessages[incomingOperation.method];
        Toaster().show({
            message,
            intent: Intent.SUCCESS,
            icon: toastIcons.info,
        });
    }
    catch (e) {
        logger.error("unable to show operation notification");
    }
}
function* handleWorkspaceStatus({ payload }) {
    try {
        const isSubscribed = yield select(isSubscribedSelector);
        if (!isSubscribed) {
            const initialWorkspaceData = yield select(getWorkspaceStateSelector);
            const layoutState = yield select(workspaceLayoutSelector);
            const duplicatedResultSetId = yield select(duplicatedResultSetIdSelector);
            const updatedLayout = prepareWorkspaceLayout(payload.workspace, layoutState, duplicatedResultSetId);
            yield call(setInitialAnalyseData, { ...initialWorkspaceData, workspace: payload.workspace });
            yield put(loadWorkspaceLayoutSuccessful(updatedLayout));
        }
        const workspace = yield select(getWorkspaceStateSelector);
        yield put(loadWorkspaceSuccessful({ ...workspace, workspace: payload.workspace }));
        yield put(setSocketStatus(SocketStatus.ACTUAL));
        yield call(showSocketOperationNotificationsSaga, payload);
        yield put(removeSocketPendingOperation(payload.requestId));
        const newAnalysisId = getNewItemId(workspace.workspace?.analyses, payload.workspace?.analyses);
        if (newAnalysisId !== false && isSubscribed) {
            yield put(setScenarioId({ id: newAnalysisId, isNew: true }));
            return;
        }
        const newResultSetId = getNewItemId(workspace.workspace?.resultSets, payload.workspace?.resultSets);
        if (newResultSetId !== false && isSubscribed) {
            yield put(setResultSetId({ id: newResultSetId, isNew: true }));
            return;
        }
        for (const resultSetIndex in workspace.workspace?.resultSets || []) {
            const currentResultElements = workspace.workspace?.resultSets[resultSetIndex]?.resultElements;
            const updatedResultElements = payload.workspace?.resultSets[resultSetIndex]?.resultElements;
            if (!currentResultElements ||
                !updatedResultElements ||
                updatedResultElements.tableElements.length < currentResultElements.tableElements.length ||
                updatedResultElements.lineGraphElements.length < currentResultElements.lineGraphElements.length ||
                updatedResultElements.heatMapElements.length < currentResultElements.heatMapElements.length ||
                updatedResultElements.metadataElements.length < currentResultElements.metadataElements.length) {
                break;
            }
            const tableElementId = getNewItemId(currentResultElements?.tableElements, updatedResultElements?.tableElements);
            if (tableElementId !== false && workspace.currentResultSetId !== null && isSubscribed) {
                yield put(setResultSetElement({ id: tableElementId, type: EngineResultType.Result0D, isNew: true }));
                return;
            }
            const lineGraphId = getNewItemId(currentResultElements?.lineGraphElements, updatedResultElements?.lineGraphElements);
            if (lineGraphId !== false && workspace.currentResultSetId !== null && isSubscribed) {
                yield put(setResultSetElement({ id: lineGraphId, type: EngineResultType.Result1D, isNew: true }));
                return;
            }
            const heatMapId = getNewItemId(currentResultElements?.heatMapElements, updatedResultElements?.heatMapElements);
            if (heatMapId !== false && workspace.currentResultSetId !== null && isSubscribed) {
                yield put(setResultSetElement({ id: heatMapId, type: EngineResultType.Result2D, isNew: true }));
                return;
            }
            const metadataElementId = getNewItemId(currentResultElements?.metadataElements, updatedResultElements?.metadataElements);
            if (metadataElementId !== false && workspace.currentResultSetId !== null && isSubscribed) {
                yield put(setResultSetElement({ id: metadataElementId, type: EngineResultType.ResultMetadata, isNew: true }));
                return;
            }
        }
        for (const analysisIndex in workspace.workspace?.analyses || []) {
            const analysisId = workspace.workspace?.analyses[analysisIndex].id;
            if (analysisId === undefined ||
                !workspace.workspace?.analyses ||
                payload.workspace.analyses.length < workspace.workspace.analyses.length) {
                break;
            }
            const newConfigurationId = getNewItemId(workspace.workspace?.analyses[analysisIndex].configurations, payload.workspace?.analyses[analysisIndex].configurations);
            if (newConfigurationId !== false && isSubscribed) {
                yield put(setScenarioId({ id: analysisId }));
                yield put(setScenarioEntity({ id: newConfigurationId, type: ScenarioEntityType.Configuration, isNew: true }));
                return;
            }
            const newSimpleMetricId = getNewItemId(workspace.workspace?.analyses[analysisIndex].absoluteRiskProfiles, payload.workspace?.analyses[analysisIndex].absoluteRiskProfiles);
            if (newSimpleMetricId !== false && isSubscribed) {
                yield put(setScenarioId({ id: analysisId }));
                yield put(setScenarioEntity({ id: newSimpleMetricId, type: ScenarioEntityType.SimpleMetric, isNew: true }));
                return;
            }
            const newRelativeMetricId = getNewItemId(workspace.workspace?.analyses[analysisIndex].relativeRiskProfiles, payload.workspace?.analyses[analysisIndex].relativeRiskProfiles);
            if (newRelativeMetricId !== false && isSubscribed) {
                yield put(setScenarioId({ id: analysisId }));
                yield put(setScenarioEntity({ id: newRelativeMetricId, type: ScenarioEntityType.RelativeMetric, isNew: true }));
                return;
            }
        }
    }
    catch (error) {
        yield put(setSocketStatus(SocketStatus.ERROR));
        logger.error(error);
    }
}
function* getRiskTemplateAnnotationsSaga({ payload }) {
    try {
        yield put(setSocketError(null));
        yield put(setSocketStatus(SocketStatus.UPDATING));
        yield call([WSService.getInstance(), "send"], UPDATE_WORKSPACE_SOCKET_METHODS.GetRiskTemplateAnnotations, payload);
    }
    catch (e) {
        yield put(getMetricTemplateAnnotationsFailure(e));
        logger.error("fetch risk template annotations is failed");
    }
}
export function* linkTagsSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        let tags = [];
        const existingTags = payload.tags.filter((tag) => tag.value !== undefined).map((tag) => tag.value);
        const tagsToCreate = payload.tags.filter((tag) => tag.value === undefined).map((tag) => tag.label);
        if (tagsToCreate.length > 0) {
            const tagsData = yield all(tagsToCreate.map((tag) => call([backendClient, PointScalingApiMethods.CreatePsTag], tag)));
            tags = [...existingTags, ...tagsData.map((tag) => tag.id)].filter((id) => id !== undefined);
        }
        else {
            tags = existingTags;
        }
        yield call([backendClient, PointScalingApiMethods.LinkPsTags], {
            entityId: payload.entityId,
            tagIds: tags,
        });
        yield put(linkTagsSuccessful());
        const data = yield call([backendClient, PointScalingApiMethods.GetPsWorkspace], payload.entityId);
        yield put(updateWorkspaceTagsSuccessful(data.tags));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "link tags failed");
        yield put(linkTagsFailure(e));
        throw e;
    }
}
export function* unlinkTagsSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield all(payload.tagIds.map((tagId) => call([backendClient, PointScalingApiMethods.UnlinkPsTag], { entityId: payload.entityId, tagId })));
        yield put(unlinkTagsSuccessful());
        if (payload.entityId !== undefined) {
            const data = yield call([backendClient, PointScalingApiMethods.GetPsWorkspace], payload.entityId);
            yield put(updateWorkspaceTagsSuccessful(data.tags));
        }
    }
    catch (e) {
        yield call(apiErrorHandler, e, "unlink tags failed");
        yield put(unlinkTagsFailure(e));
        throw e;
    }
}
function* preparePrintCanvasContent({ payload }) {
    try {
        const { wrapper, elements } = payload;
        const clonedNode = wrapper.cloneNode(false);
        const preparedElements = yield all(elements
            .sort((a, b) => a.index - b.index)
            .map(({ element, isGraph }) => (isGraph ? getGraphPrintContent(element) : element.cloneNode(true))));
        preparedElements.forEach((el) => {
            clonedNode.appendChild(el);
        });
        yield put(setPrintCanvasContent(clonedNode));
    }
    catch (e) {
        yield put(removePrintCanvasContent());
        logger.error("Prepare print canvas content failed");
        throw e;
    }
}
function* prepareCsvDataForTabularResults(resultSetId, resultSetElementId) {
    const results = yield select(calculationResultsTabularSelector(resultSetId, resultSetElementId));
    const layout = yield select(tabularViewLayoutSelector(resultSetId, resultSetElementId));
    return convertTabularResultsToCsv(results, layout);
}
function* prepareCsvDataForLinegraphResults(resultSetId, resultSetElementId) {
    const { lines = [] } = yield select(calculationResultsLineGraphSelector(resultSetId, resultSetElementId));
    const exportData = yield select(lineGraphElementExportDataSelector(resultSetId, resultSetElementId));
    const results = prepareLineGraphData([], lines, { responses: exportData.responses }, {});
    return convertLinegraphResultsToCsv(results, exportData);
}
function* prepareCsvDataForHeatmapResults(resultSetId, resultSetElementId) {
    const { contours = [] } = yield select(calculationResultsHeatmapSelector(resultSetId, resultSetElementId));
    const exportData = yield select(heatmapElementExportDataSelector(resultSetId, resultSetElementId));
    const results = prepareHeatMapData([], contours, { responses: exportData.responses }, {});
    return convertHeatmapResultsToCsv(results, exportData);
}
function* prepareCsvDataForMetadata(resultSetId, resultSetElementId) {
    const metadata = yield select(calculationResultsMetadataSelector(resultSetId, resultSetElementId));
    const metadataElement = yield select(metadataResultSetElementByIdSelector(resultSetId, resultSetElementId));
    const layout = yield select(workspaceLayoutSelector);
    const { configurations = [] } = layout
        .find(({ id }) => resultSetId === id)
        ?.metadataElements.find(({ id }) => parseInt(id) === resultSetElementId) || {};
    return convertMetadataToCsv(metadata, metadataElement, configurations);
}
function* exportResultsSaga({ payload }) {
    try {
        let csvData = "";
        switch (payload.type) {
            case EngineResultType.Result0D:
                csvData = yield call(prepareCsvDataForTabularResults, payload.resultSetId, payload.resultSetElementId);
                break;
            case EngineResultType.Result1D:
                csvData = yield call(prepareCsvDataForLinegraphResults, payload.resultSetId, payload.resultSetElementId);
                break;
            case EngineResultType.Result2D:
                csvData = yield call(prepareCsvDataForHeatmapResults, payload.resultSetId, payload.resultSetElementId);
                break;
            case EngineResultType.ResultMetadata:
                csvData = yield call(prepareCsvDataForMetadata, payload.resultSetId, payload.resultSetElementId);
                break;
        }
        if (!csvData) {
            throw new Error();
        }
        const fileToSave = new Blob([csvData], { type: "csv" });
        saveAs(fileToSave, `${payload.filename}.csv`);
    }
    catch (e) {
        logger.error("Export results failed");
    }
    finally {
        yield put(exportResultsFinish());
    }
}
function* scenarioExportSaga({ payload }) {
    try {
        const workspaceId = yield select(workspaceIdSelector);
        if (workspaceId === null) {
            throw new Error();
        }
        const backendClient = BackendClient.getInstance();
        const exportedData = yield call([backendClient, PointScalingApiMethods.ExportScenario], workspaceId, payload);
        yield put(scenarioExportFinish());
        if (!exportedData) {
            throw new Error();
        }
        const json = JSON.stringify(exportedData);
        const fileToSave = new Blob([json], { type: "json" });
        saveAs(fileToSave, `${getCurrentDateTimeStamp()}_${exportedData.analysis.name}.json`);
    }
    catch (e) {
        yield call(apiErrorHandler, e, "export scenario failed");
    }
    finally {
        yield put(scenarioExportFinish());
    }
}
let resultElementsLoadingTimers = [];
function* handleResultElementsLoadingStatusChange(payload) {
    const { isLoading, ...restData } = payload;
    yield delay(isLoading ? LONG_CALCULATION_DELAY : 0);
    if (isLoading) {
        yield put(setResultElementWithLongCalculation(restData));
    }
    else {
        yield put(removeResultElementsWithLongCalculation(restData));
    }
}
function* checkResultElementsLongCalculation({ payload, }) {
    try {
        const { data, resultSetElementId, resultSetId, type } = payload;
        const resultElement = { resultSetElementId, resultSetId, type };
        let isLoading = false;
        if (type === EngineResultType.Result0D) {
            isLoading = data.some((item) => item.some((i) => i.state === PsCalculationResultsDataItemState.Pending));
        }
        else if (type === EngineResultType.Result1D) {
            isLoading = data.some((item) => item.state === PsCalculationResultsDataItemState.Pending);
        }
        else if (type === EngineResultType.Result2D) {
            isLoading = data.some((item) => item.some(({ state }) => state === PsCalculationResultsDataItemState.Pending));
        }
        const storedTimer = resultElementsLoadingTimers.find((timer) => resultSetId === timer.resultSetId &&
            resultSetElementId === timer.resultSetElementId &&
            type === timer.type);
        const filteredTimers = resultElementsLoadingTimers.filter((timer) => type !== timer.type ||
            resultSetId !== timer.resultSetId ||
            (resultSetId === timer.resultSetId && resultSetElementId !== timer.resultSetElementId));
        const timerFn = yield call(handleResultElementsLoadingStatusChange, {
            ...resultElement,
            isLoading,
        });
        if (!storedTimer) {
            resultElementsLoadingTimers.push({ ...resultElement, isLoading, timerFn });
        }
        else if (storedTimer && !isLoading) {
            resultElementsLoadingTimers = [...filteredTimers];
        }
    }
    catch (e) {
        logger.error(e);
    }
}
export default function* workspaceSaga() {
    yield takeLatest(WorkspaceActionType.WORKSPACE_GO_BACK_HANDLE, goBackHandleSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_LOAD, loadWorkspace);
    yield takeLatest(WorkspaceActionType.WORKSPACE_CREATE, createWorkspace);
    yield takeLatest(WorkspaceActionType.WORKSPACE_SOCKET_STATUS, handleWorkspaceStatus);
    yield takeLatest(WorkspaceActionType.WORKSPACE_SOCKET_RECONNECTED, resubscribeWorkspaceSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_UPDATE, updateWorkspace);
    yield takeLatest(WorkspaceActionType.SOCKET_SUBSCRIBE_MESSAGE, handleSubscribeMessageStatus);
    yield takeLatest(WorkspaceActionType.GET_METRIC_TEMPLATE_ANNOTATIONS, getRiskTemplateAnnotationsSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_UPDATE_NAME, updateWorkspaceNameSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_UPDATE_DESCRIPTION, updateWorkspaceDescriptionSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_LINK_TAGS, linkTagsSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_UNLINK_TAGS, unlinkTagsSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_PREPARE_PRINT_CANVAS_CONTENT, preparePrintCanvasContent);
    yield takeLatest(WorkspaceActionType.WORKSPACE_LOAD_LAYOUT, loadWorkspaceLayout);
    yield takeLatest(WorkspaceActionType.WORKSPACE_EXPORT_RESULTS, exportResultsSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_SCENARIO_EXPORT, scenarioExportSaga);
    yield takeLatest(WorkspaceActionType.WORKSPACE_SET_CALCULATION_HEATMAP_RESULTS, checkResultElementsLongCalculation);
    yield takeLatest(WorkspaceActionType.WORKSPACE_SET_CALCULATION_LINE_GRAPH_RESULTS, checkResultElementsLongCalculation);
    yield takeLatest(WorkspaceActionType.WORKSPACE_SET_CALCULATION_TABULAR_RESULTS, checkResultElementsLongCalculation);
}
